import { NgModule } from '@angular/core';
import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { RouterModule, Routes } from '@angular/router';
import { AuthGuard } from './core/guards/auth-guard.service';
import { easyshipEnabledGuard } from './core/guards/easyship-enabled.guard';
import { MonitorModeRedirectGuard } from './core/guards/monitor-mode-redirect.guard';
import { PermissionGuard } from './core/guards/permission.guard';
import { SsoScopeGuard } from './core/guards/sso-scope.guard';

const routes: Routes = [
  {
    path: '',
    redirectTo: 'auth/login',
    pathMatch: 'full',
  },
  {
    path: 'auth',
    title: 'Sign In',
    loadChildren: () =>
      import('./features/auth/auth.module').then((m) => m.AuthModule),
  },
  {
    canActivate: [AuthGuard, MonitorModeRedirectGuard, PermissionGuard, SsoScopeGuard],
    data: { subject: 'DashboardPage', action: 'view' },
    path: 'dashboard',
    title: 'Dashboard',
    loadChildren: () =>
      import('./features/dashboard/dashboard.module').then(
        (m) => m.DashboardModule
      ),
  },
  {
    canActivate: [AuthGuard, MonitorModeRedirectGuard, PermissionGuard, SsoScopeGuard],
    data: { subject: 'PatientsPage', action: 'view' },
    path: 'patients',
    title: 'Patients',
    loadChildren: () =>
      import('./features/patients/patients.module').then(
        (m) => m.PatientsModule
      ),
  },
  {
    canActivate: [AuthGuard, MonitorModeRedirectGuard, PermissionGuard, SsoScopeGuard],
    data: { subject: 'WorkingAreaPage', action: 'view' },
    path: 'trend-analysis',
    title: 'Trend Analysis',
    loadChildren: () =>
      import('./trend-analysis/trend-analysis.module').then(
        (m) => m.TrendAnalysisModule
      ),
  },
  {
    canActivate: [AuthGuard, MonitorModeRedirectGuard, PermissionGuard, SsoScopeGuard],
    data: { subject: 'ReportsPage', action: 'view' },
    path: 'statistics',
    title: 'Statistics',
    loadChildren: () =>
      import('./features/reports/reports.module').then((m) => m.ReportsModule),
  },
  {
    canActivate: [AuthGuard, PermissionGuard, SsoScopeGuard],
    data: { subject: 'LiveTestsPage', action: 'view' },
    path: 'live-tests',
    title: 'Live Tests',
    loadChildren: () =>
      import('./features/live-tests/live-tests.module').then(
        (m) => m.LiveTestsModule
      ),
  },
  {
    canActivate: [AuthGuard, MonitorModeRedirectGuard, PermissionGuard, SsoScopeGuard],
    data: { subject: 'DevicesPage', action: 'view' },
    path: 'devices',
    title: 'Devices',
    loadChildren: () =>
      import('./features/devices/devices.module').then((m) => m.DeviceModule),
  },
  {
    // The catalogues behind device shipping: dimensions, boxes, packing rules,
    // origins. Shipping roles only — checked as EDIT, because a Viewer can view
    // every subject and must not reach shipping — and only with Easyship on.
    canActivate: [AuthGuard, MonitorModeRedirectGuard, PermissionGuard, SsoScopeGuard, easyshipEnabledGuard],
    data: { subject: 'DeviceShipment', action: 'edit' },
    path: 'device-shipping/settings',
    title: 'Shipping Settings',
    loadComponent: () =>
      import('./features/shipping/shipping-settings.component').then(
        (m) => m.ShippingSettingsComponent
      ),
  },
  {
    // Shipments and tracking are a tab of the Devices page now. The old address
    // still works for anyone who saved it; the tab is offered to the shipping
    // roles only, so for anyone else this is simply the Devices page.
    path: 'device-shipping',
    pathMatch: 'full',
    redirectTo: '/devices?tab=shipments',
  },
  {
    canActivate: [AuthGuard, MonitorModeRedirectGuard, PermissionGuard, SsoScopeGuard],
    data: { subject: 'SettingsPage', action: 'view' },
    path: 'settings',
    title: 'Settings',
    loadChildren: () =>
      import('./features/settings/settings.module').then(
        (m) => m.SettingsModule
      ),
  },
  {
    canActivate: [AuthGuard, MonitorModeRedirectGuard, PermissionGuard, SsoScopeGuard],
    canActivateChild: [SsoScopeGuard],
    data: { subject: 'WorkingAreaPage', action: 'view' },
    path: 'working-area',
    title: 'Working Area',
    loadChildren: () =>
      import('./features/working-area/working-area.module').then(
        (m) => m.WorkingAreaModule
      ),
  },
  {
    canActivate: [AuthGuard, MonitorModeRedirectGuard, PermissionGuard, SsoScopeGuard],
    data: { subject: 'TestsPage', action: 'view' },
    path: 'tests',
    title: 'Tests',
    loadChildren: () =>
      import('./features/tests/tests.module').then((m) => m.TestsModule),
  },
  {
    canActivate: [AuthGuard, MonitorModeRedirectGuard, PermissionGuard, SsoScopeGuard],
    data: { subject: 'TestsPage', action: 'view' },
    path: 'tests-records',
    title: 'Test Records',
    loadChildren: () =>
      import('./features/tests/features/test-records.module').then(
        (m) => m.TestRecordsModule
      ),
  },

  {
    canActivate: [AuthGuard, MonitorModeRedirectGuard, PermissionGuard, SsoScopeGuard],
    data: { subject: 'PhysicianDashboardPage', action: 'view' },
    path: 'physician-dashboard',
    title: 'Physician Dashboard',
    loadChildren: () =>
      import('./features/physician-dashboard/physician-dashboard.module').then(
        (m) => m.PhysicianDashboardModule
      ),
  },

  {
    canActivate: [AuthGuard, MonitorModeRedirectGuard, SsoScopeGuard],
    path: 'checkout',
    title: 'Checkout',
    loadComponent: () =>
      import('./features/payment/payment.component').then(
        (m) => m.PaymentComponent
      ),
  },

  {
    canActivate: [AuthGuard, MonitorModeRedirectGuard, SsoScopeGuard],
    path: 'payment-success',
    title: 'Payment Complete',
    loadComponent: () =>
      import('./features/payment-success/payment-success.component').then(
        (m) => m.PaymentSuccessComponent
      ),
  },

  {
    path: 'pdf-preview',
    title: 'Report Preview',
    loadComponent: () =>
      import(
        './features/working-area/components/working-area2/generate-reports/pdf-preview/pdf-preview-main/pdf-preview.component'
      ).then((m) => m.PDFPreview),
  },

  {
    // Patient-facing research consent (IRB) form, opened from an emailed link.
    // No guard: the token in the query string is the credential, and the patient
    // has no account to sign in with.
    path: 'research-consent',
    title: 'Research Consent',
    loadComponent: () =>
      import('./features/research-consent/research-consent.component').then(
        (m) => m.ResearchConsentComponent
      ),
  },

  {
    path: 'physician/sso-forward',
    title: 'Opening Test',
    loadComponent: () =>
      import(
        './features/physician-sso-forward/physician-sso-forward.component'
      ).then((m) => m.PhysicianSsoForwardComponent),
  },
];

@NgModule({
  imports: [
    RouterModule.forRoot(routes, { onSameUrlNavigation: 'reload' }),
    BrowserModule,
    BrowserAnimationsModule,
  ],
  exports: [RouterModule],
})
export class AppRoutingModule {}
